/* eslint-disable no-console */
import React, { useEffect, useMemo, useCallback, useReducer, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Select, { DropdownIndicatorProps, GroupBase, CSSObjectWithLabel, MenuListProps } from 'react-select';

import MenuListVirtualized from 'components/MenuListVirtualized';
import { rootDocument as rootDocumentSelector } from 'containers/RootDocument/selectors';
import { getBinderBusinessUnits, getCrmDetailGroups, getCrmProducts } from 'services/api';
import { intlGet } from 'utils/intlGet';
import {
  binderFormReducer,
  initialBinderFormState,
  SelectOption,
  BinderFormAction,
  BinderFormSelectType,
  BinderFormActionType,
  MAX_LINE_LENGTH,
} from './binderFormReducer';
import { ManageBinderProps } from './models';
import styles from './styles.module.scss';

interface CRMProductAPI {
  id: string;
  name__v: string;
}
interface BusinessUnitAPI {
  name: string;
  label: string;
}

const selectControlStyles = {
  minHeight: '30px',
  maxHeight: '100px',
  overflow: 'auto',
};

const getDropdownIndicatorStyles = (state: DropdownIndicatorProps<SelectOption, false, GroupBase<SelectOption>>): Record<string, string> => {
  return {
    transform: state?.selectProps?.menuIsOpen ? 'rotate(180deg)' : 'none',
  };
};

const customStyles = {
  control: (baseStyles: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...baseStyles,
    ...selectControlStyles,
  }),
  dropdownIndicator: (
    provided: CSSObjectWithLabel,
    state: DropdownIndicatorProps<SelectOption, false, GroupBase<SelectOption>>,
  ): CSSObjectWithLabel => {
    return {
      ...provided,
      ...getDropdownIndicatorStyles(state),
    };
  }, multiValue: (baseStyles: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...baseStyles,
    backgroundColor: 'rgba(0, 114, 220, 0.1)' }),
};

const DROPDOWN_HEIGHT_LARGE = 150;
const DROPDOWN_HEIGHT_SMALL = 100;

// wrapper components for the MenuList with different max heights
const MenuListLarge = (
  props: MenuListProps<SelectOption, true, GroupBase<SelectOption>>,
): JSX.Element => <MenuListVirtualized {...props} maxHeight={DROPDOWN_HEIGHT_LARGE} />;
const MenuListSmall = (
  props: MenuListProps<SelectOption, true, GroupBase<SelectOption>>,
): JSX.Element => <MenuListVirtualized {...props} maxHeight={DROPDOWN_HEIGHT_SMALL} />;

const ManageBinder: React.FunctionComponent<ManageBinderProps> = ({
  showUploadProjectModal,
  hideModal,
  createBinderAndUpload,
  uploadContext,
}): JSX.Element => {
  const [state, dispatch] = useReducer(binderFormReducer, initialBinderFormState);

  const [crmProductsData, setCrmProductsData] = useState<CRMProductAPI[] | null>(null);
  const [businessUnitsData, setBusinessUnitsData] = useState<BusinessUnitAPI[] | null>(null);
  const [crmDetailGroupsData, setCrmDetailGroupsData] = useState<CRMProductAPI[] | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  const rootDocument = useSelector(rootDocumentSelector);
  const rootDocumentId = rootDocument.get('documentId');

  const formLabelPath = 'ManageBinderWindow.Form';
  const buttonTextPath = 'ManageBinderWindow.Button';
  const isSubmitDisabled = !state.isValid;

  const onClose = (): void => {
    showUploadProjectModal();
  };

  const handleBinderCreate = (): void => {
    if (state.isValid) {

      if (uploadContext && createBinderAndUpload) {
        createBinderAndUpload(
          state,
          uploadContext.uploadProjectType,
          uploadContext.translationComplete,
        );
      } else {
        showUploadProjectModal();
      }
      hideModal();
    } else {
      showUploadProjectModal();
    }
  };
  const fetchBinderValues = useCallback((): void => {
    setIsFetching(true);

    const fetchPromises = [
      getCrmProducts(rootDocumentId)
        .then((crmProducts) => {
          if (crmProducts && Array.isArray(crmProducts)) {
            setCrmProductsData(crmProducts as CRMProductAPI[]);
          }
        })
        .catch((error) => {
          console.error('Error fetching CRM products:', error);
        }),

      getBinderBusinessUnits()
        .then((businessUnits) => {
          if (businessUnits && Array.isArray(businessUnits)) {
            setBusinessUnitsData(businessUnits as BusinessUnitAPI[]);
          }
        })
        .catch((error) => {
          console.error('Error fetching business units:', error);
        }),
    ];

    Promise.all(fetchPromises.map(p => p.catch(e => e))) .finally(() => {
      setIsFetching(false);
    });
  }, [rootDocumentId]);

  const fetchCrmDetailGroups = useCallback((): void => {
    const selectedCrmProductsIds = state.crmProducts?.map(product => product.value) ?? [];
    const isDetailGroupSelectFilled = Boolean(state.crmDetailGroup);

    if (selectedCrmProductsIds.length > 0) {
      getCrmDetailGroups(selectedCrmProductsIds)
        .then((crmDetailGroups) => {
          if (crmDetailGroups && Array.isArray(crmDetailGroups)) {
            setCrmDetailGroupsData(crmDetailGroups as CRMProductAPI[]);

            const newResponseIncludesSelectedDetailGroup = crmDetailGroups.some((group: CRMProductAPI) => group.id === state.crmDetailGroup?.value);

            if (isDetailGroupSelectFilled && !newResponseIncludesSelectedDetailGroup) {
              dispatch({ type: BinderFormActionType.SET_CRM_DETAIL_GROUP, payload: null });
            }
          }
        })
        .catch((error) => {
          console.error('Error fetching CRM detail groups:', error);
        });
    } else {
      setCrmDetailGroupsData(null);
      if (isDetailGroupSelectFilled) {
        dispatch({ type: BinderFormActionType.SET_CRM_DETAIL_GROUP, payload: null });
      }
    }
  }, [state.crmDetailGroup, state.crmProducts]);

  const handleSelect = <T extends BinderFormSelectType>(
    value: T extends BinderFormActionType.SET_CRM_DETAIL_GROUP ? SelectOption | null : SelectOption[] | null,
    type: T,
  ): void => {
    dispatch({ type, payload: value } as BinderFormAction);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    dispatch({ type: BinderFormActionType.SET_BINDER_NAME, payload: e.target.value });
  };

  const crmProducts = useMemo(() => {
    return crmProductsData?.map((product) => {
      return {
        value: product.id,
        label: product.name__v,
      };
    }) ?? [];
  }, [crmProductsData]);

  const businessUnits = useMemo(() => {
    return businessUnitsData?.map((unit) => {
      return {
        value: unit.name,
        label: unit.label,
      };
    }) ?? [];
  }, [businessUnitsData]);

  const crmDetailGroups = useMemo(() => {
    return crmDetailGroupsData?.map((group) => {
      return {
        value: group.id,
        label: group.name__v,
      };
    }) ?? [];
  }, [crmDetailGroupsData]);

  const crmDetailGroupsNoOptionsMessage = useMemo(() => {
    return crmDetailGroups?.length === 0
      ? intlGet(formLabelPath, 'CrmDetailGroupNoOptions')
      : intlGet(formLabelPath, 'NoOptionsDefault');
  }, [crmDetailGroups]);

  useEffect(() => {
    fetchCrmDetailGroups();
  }, [state.crmProducts, fetchCrmDetailGroups]);

  useEffect(() => {
    fetchBinderValues();
  }, [fetchBinderValues]);

  return (
    <div className={styles.ManageBinder}>
      <header>
        <h3 className={styles.title}>{intlGet('ManageBinderWindow', 'Title')}</h3>
        <p className={styles.subtitle}>{intlGet('ManageBinderWindow', 'Subtitle')}</p>
      </header>
      <section>
        <Form>
          <Form.Group className="mb-3" controlId="binderName">
            <Form.Label>
              {intlGet(formLabelPath, 'Name')}
              <sup className={styles.required}> *</sup>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={state.binderName.length > MAX_LINE_LENGTH ? 2 : 1}
              value={state.binderName}
              required={true}
              onChange={handleNameChange}
              className={styles.textarea}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="crmProduct">
            <Form.Label>
              {intlGet(formLabelPath, 'CrmProduct')}
              <sup className={styles.required}> *</sup>
            </Form.Label>
            <Select<SelectOption, true>
              id="crmProduct"
              isMulti={true}
              options={crmProducts}
              placeholder='Start typing to find the value'
              noOptionsMessage={(): string => intlGet(formLabelPath, 'NoOptionsDefault')}
              isSearchable={true}
              isLoading={isFetching}
              value={state.crmProducts}
              onChange={(selected: SelectOption[] | null): void =>
                handleSelect<BinderFormActionType.SET_CRM_PRODUCTS>(
                  selected,
                  BinderFormActionType.SET_CRM_PRODUCTS,
                )
              }
              components={{
                MenuList: MenuListLarge,
                IndicatorSeparator: null,
              }}
              isClearable={false}
              required={true}
              styles={customStyles}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="crmDetailGroup">
            <Form.Label>{intlGet(formLabelPath, 'CrmDetailGroup')}</Form.Label>
            <Select<SelectOption, true>
              id="crmDetailGroup"
              options={crmDetailGroups}
              isSearchable={true}
              value={state.crmDetailGroup}
              placeholder='Depends on CRM Product'
              noOptionsMessage={(): string => crmDetailGroupsNoOptionsMessage}
              onChange={(selected: SelectOption | null): void =>
                handleSelect<BinderFormActionType.SET_CRM_DETAIL_GROUP>(
                  selected,
                  BinderFormActionType.SET_CRM_DETAIL_GROUP,
                )
              }
              components={{
                MenuList: MenuListLarge,
                IndicatorSeparator: null,
              }}
              isClearable={true}
              styles={customStyles}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="businessUnit">
            <Form.Label>{intlGet(formLabelPath, 'BusinessUnit')}</Form.Label>
            <Select<SelectOption, true>
              id="businessUnit"
              isMulti={true}
              options={businessUnits}
              isSearchable={true}
              isLoading={isFetching}
              value={state.businessUnits}
              noOptionsMessage={(): string => intlGet(formLabelPath, 'NoOptionsDefault')}
              onChange={(selected: SelectOption[] | null): void =>
                handleSelect<BinderFormActionType.SET_BUSINESS_UNITS>(
                  selected,
                  BinderFormActionType.SET_BUSINESS_UNITS,
                ) }
              components={{
                MenuList: MenuListSmall,
                IndicatorSeparator: null,
              }}
              isClearable={false}
              styles={customStyles}
              placeholder=""
            />
          </Form.Group>
        </Form>
      </section>
      <footer>
        {isSubmitDisabled && <p>{intlGet(buttonTextPath, 'CreateBinderHelper')}</p>}
        <div className={styles.btns}>
          <Button variant='secondary' onClick={onClose}>{intlGet(buttonTextPath, 'Cancel')}</Button>
          <Button
            variant='primary'
            disabled={isSubmitDisabled}
            onClick={handleBinderCreate}
            key='submitBinder'
            className={styles.btnsSubmit}
          >
            {intlGet(buttonTextPath, 'CreateBinder')}
          </Button>
        </div>
      </footer>
    </div>
  );
};

export default ManageBinder;
